import { defineStore } from 'pinia';
import { ref, watch, computed } from 'vue';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type ColorTheme = 'purple' | 'blue' | 'green' | 'orange' | 'pink' | 'custom';

export interface ThemeColors {
  primary: string;
  primaryLight: string;
  secondary: string;
  bg: string;
  bgSecondary: string;
  bgTertiary: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  border: string;
}

export interface ColorThemeConfig {
  id: ColorTheme;
  name: string;
  icon: string;
  light: ThemeColors;
  dark: ThemeColors;
}

// 预设颜色主题
export const colorThemes: Record<ColorTheme, ColorThemeConfig> = {
  purple: {
    id: 'purple',
    name: '紫色',
    icon: '💜',
    light: {
      primary: '#667eea',
      primaryLight: '#818cf8',
      secondary: '#764ba2',
      bg: '#f5f5f5',
      bgSecondary: '#ffffff',
      bgTertiary: '#f3f4f6',
      text: '#1f2937',
      textSecondary: '#6b7280',
      textTertiary: '#d1d5db',
      border: '#e5e7eb',
    },
    dark: {
      primary: '#818cf8',
      primaryLight: '#a5b4fc',
      secondary: '#764ba2',
      bg: '#111827',
      bgSecondary: '#1f2937',
      bgTertiary: '#374151',
      text: '#f9fafb',
      textSecondary: '#e5e7eb',
      textTertiary: '#d1d5db',
      border: '#4b5563',
    },
  },
  blue: {
    id: 'blue',
    name: '蓝色',
    icon: '💙',
    light: {
      primary: '#3b82f6',
      primaryLight: '#60a5fa',
      secondary: '#2563eb',
      bg: '#f5f5f5',
      bgSecondary: '#ffffff',
      bgTertiary: '#f3f4f6',
      text: '#1f2937',
      textSecondary: '#6b7280',
      textTertiary: '#d1d5db',
      border: '#e5e7eb',
    },
    dark: {
      primary: '#60a5fa',
      primaryLight: '#93c5fd',
      secondary: '#2563eb',
      bg: '#111827',
      bgSecondary: '#1f2937',
      bgTertiary: '#374151',
      text: '#f9fafb',
      textSecondary: '#e5e7eb',
      textTertiary: '#d1d5db',
      border: '#4b5563',
    },
  },
  green: {
    id: 'green',
    name: '绿色',
    icon: '💚',
    light: {
      primary: '#10b981',
      primaryLight: '#34d399',
      secondary: '#059669',
      bg: '#f5f5f5',
      bgSecondary: '#ffffff',
      bgTertiary: '#f3f4f6',
      text: '#1f2937',
      textSecondary: '#6b7280',
      textTertiary: '#d1d5db',
      border: '#e5e7eb',
    },
    dark: {
      primary: '#34d399',
      primaryLight: '#6ee7b7',
      secondary: '#059669',
      bg: '#111827',
      bgSecondary: '#1f2937',
      bgTertiary: '#374151',
      text: '#f9fafb',
      textSecondary: '#e5e7eb',
      textTertiary: '#d1d5db',
      border: '#4b5563',
    },
  },
  orange: {
    id: 'orange',
    name: '橙色',
    icon: '🧡',
    light: {
      primary: '#f59e0b',
      primaryLight: '#fbbf24',
      secondary: '#d97706',
      bg: '#f5f5f5',
      bgSecondary: '#ffffff',
      bgTertiary: '#f3f4f6',
      text: '#1f2937',
      textSecondary: '#6b7280',
      textTertiary: '#d1d5db',
      border: '#e5e7eb',
    },
    dark: {
      primary: '#fbbf24',
      primaryLight: '#fcd34d',
      secondary: '#d97706',
      bg: '#111827',
      bgSecondary: '#1f2937',
      bgTertiary: '#374151',
      text: '#f9fafb',
      textSecondary: '#e5e7eb',
      textTertiary: '#d1d5db',
      border: '#4b5563',
    },
  },
  pink: {
    id: 'pink',
    name: '粉色',
    icon: '💗',
    light: {
      primary: '#ec4899',
      primaryLight: '#f472b6',
      secondary: '#db2777',
      bg: '#f5f5f5',
      bgSecondary: '#ffffff',
      bgTertiary: '#f3f4f6',
      text: '#1f2937',
      textSecondary: '#6b7280',
      textTertiary: '#d1d5db',
      border: '#e5e7eb',
    },
    dark: {
      primary: '#f472b6',
      primaryLight: '#f9a8d4',
      secondary: '#db2777',
      bg: '#111827',
      bgSecondary: '#1f2937',
      bgTertiary: '#374151',
      text: '#f9fafb',
      textSecondary: '#e5e7eb',
      textTertiary: '#d1d5db',
      border: '#4b5563',
    },
  },
  custom: {
    id: 'custom',
    name: '自定义',
    icon: '🎨',
    light: {
      primary: '#667eea',
      primaryLight: '#818cf8',
      secondary: '#764ba2',
      bg: '#f5f5f5',
      bgSecondary: '#ffffff',
      bgTertiary: '#f3f4f6',
      text: '#1f2937',
      textSecondary: '#6b7280',
      textTertiary: '#d1d5db',
      border: '#e5e7eb',
    },
    dark: {
      primary: '#818cf8',
      primaryLight: '#a5b4fc',
      secondary: '#764ba2',
      bg: '#111827',
      bgSecondary: '#1f2937',
      bgTertiary: '#374151',
      text: '#f9fafb',
      textSecondary: '#e5e7eb',
      textTertiary: '#d1d5db',
      border: '#4b5563',
    },
  },
};

export const useThemeStore = defineStore('theme', () => {
  // 从 localStorage 读取保存的主题设置
  const savedTheme = localStorage.getItem('snapclip-theme') as ThemeMode | null;
  const theme = ref<ThemeMode>(savedTheme || 'auto');

  // 从 localStorage 读取保存的颜色主题
  const savedColorTheme = localStorage.getItem('snapclip-color-theme') as ColorTheme | null;
  const colorTheme = ref<ColorTheme>(savedColorTheme || 'purple');

  // 获取实际应用的主题（考虑 auto 模式）
  const resolvedTheme = ref<'light' | 'dark'>(getResolvedTheme());

  // 当前颜色主题配置
  const currentColorTheme = computed(() => {
    const themeConfig = colorThemes[colorTheme.value];
    return resolvedTheme.value === 'dark' ? themeConfig.dark : themeConfig.light;
  });

  // 监听主题变化
  watch(theme, () => {
    updateResolvedTheme();
    saveTheme();
  });

  // 监听颜色主题变化
  watch(colorTheme, () => {
    applyColorTheme();
    saveColorTheme();
  });

  // 监听系统主题变化（仅在 auto 模式下）
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (theme.value === 'auto') {
      updateResolvedTheme();
    }
  });

  function getResolvedTheme(): 'light' | 'dark' {
    if (theme.value === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme.value;
  }

  function updateResolvedTheme() {
    resolvedTheme.value = getResolvedTheme();
    applyTheme(resolvedTheme.value);
  }

  function applyTheme(themeValue: 'light' | 'dark') {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(themeValue);
    // 立即应用颜色主题，传递主题值避免依赖响应式状态
    applyColorTheme(themeValue);
  }

  function applyColorTheme(themeValue?: 'light' | 'dark') {
    // 如果没有传递主题值，使用当前的 resolvedTheme
    const currentTheme = themeValue ?? resolvedTheme.value;
    const themeConfig = colorThemes[colorTheme.value];
    const colors = currentTheme === 'dark' ? themeConfig.dark : themeConfig.light;
    const root = document.documentElement;

    // 设置 CSS 变量（变量名必须与 theme.css 中使用的一致）
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-primary-light', colors.primaryLight);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--bg-primary', colors.bg);
    root.style.setProperty('--bg-secondary', colors.bgSecondary);
    root.style.setProperty('--bg-tertiary', colors.bgTertiary);
    root.style.setProperty('--text-primary', colors.text);
    root.style.setProperty('--text-secondary', colors.textSecondary);
    root.style.setProperty('--text-tertiary', colors.textTertiary);
    root.style.setProperty('--border-color', colors.border);

    // 关键修复：使用 setProperty 并添加 'important' 来确保样式生效
    root.style.setProperty('background-color', colors.bg, 'important');
    root.style.setProperty('color', colors.text, 'important');

    // 同时设置 body 的背景色和文本颜色
    if (document.body) {
      document.body.style.setProperty('background-color', colors.bg, 'important');
      document.body.style.setProperty('color', colors.text, 'important');
    }
  }

  function setTheme(newTheme: ThemeMode) {
    theme.value = newTheme;
  }

  function setColorTheme(newColorTheme: ColorTheme) {
    colorTheme.value = newColorTheme;
  }

  function saveTheme() {
    localStorage.setItem('snapclip-theme', theme.value);
  }

  function saveColorTheme() {
    localStorage.setItem('snapclip-color-theme', colorTheme.value);
  }

  function toggleTheme() {
    if (resolvedTheme.value === 'light') {
      setTheme('dark');
    } else {
      setTheme('light');
    }
  }

  // 初始化主题
  updateResolvedTheme();

  return {
    theme,
    resolvedTheme,
    colorTheme,
    currentColorTheme,
    setTheme,
    setColorTheme,
    toggleTheme,
  };
});
